import { ReactNode } from 'react';
import { Box } from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import { REDUCED_MOTION } from 'components/ui/tokens';
import { freezeGlowRingSx } from 'features/subscription-freeze/components/freezeMotion';
import { PRESERVATION_INK, TEMPERATURE } from 'features/subscription-freeze/components/freezeUi';

export type FeaturedTone = 'primary' | 'error' | 'success' | 'ice';

// Ice is the freeze page's colour: too pale for a light dialog alone, so it glows on that page's midnight.
function toneColors(theme: Theme, tone: FeaturedTone) {
  if (tone === 'ice') {
    const { accent, soft } = TEMPERATURE.ice;
    return { main: accent, ring: soft, disc: PRESERVATION_INK.surface, glow: freezeGlowRingSx('ice') };
  }
  const { main } = theme.palette[tone];
  return { main, ring: alpha(main, 0.06), disc: alpha(main, 0.14), glow: {} };
}

const SIZES = { md: { outer: 64, inner: 44, font: 22 }, sm: { outer: 48, inner: 34, font: 17 } } as const;

type Props = {
  icon: ReactNode;
  tone: FeaturedTone;
  size?: keyof typeof SIZES;
  /** Pops in on mount, for an icon that arrives with its step. */
  animate?: boolean;
  /** One ring ripples out, for a finished action. */
  burst?: boolean;
};

const POP = {
  '@keyframes featuredPop': {
    '0%': { opacity: 0, transform: 'scale(.6)' },
    '70%': { opacity: 1, transform: 'scale(1.06)' },
    '100%': { transform: 'scale(1)' }
  },
  animation: 'featuredPop .45s cubic-bezier(.2,.7,.3,1) backwards'
} as const;

/** An icon in two soft rings of its tone: the one visual mark each cancellation surface carries. */
export default function FeaturedIcon({ icon, tone, size = 'md', animate = false, burst = false }: Props) {
  const { outer, inner, font } = SIZES[size];
  const { main, ring, disc, glow } = toneColors(useTheme(), tone);
  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        width: outer,
        height: outer,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: ring,
        ...(animate && POP),
        ...(burst && {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${alpha(main, 0.45)}`,
            animation: 'featuredBurst 1.1s .25s ease-out both',
            pointerEvents: 'none'
          },
          '@keyframes featuredBurst': {
            from: { opacity: 1, transform: 'scale(.85)' },
            to: { opacity: 0, transform: 'scale(1.7)' }
          }
        }),
        // One block for both motions: a second one would replace the first.
        [REDUCED_MOTION]: { animation: 'none', '&::after': { display: 'none' } }
      }}
    >
      <Box
        sx={{
          width: inner,
          height: inner,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: font,
          color: main,
          bgcolor: disc,
          ...glow
        }}
      >
        {icon}
      </Box>
    </Box>
  );
}
